/**
 * The normalised media event stream (spec.md §13.3, §13.6).
 *
 * Thirteen DOM media events go in; one typed event carrying a fully scrubbed snapshot
 * comes out. The store mirrors that stream into React state, `recovery.ts` watches it for
 * stalls, and nothing else in the app listens to the element directly.
 *
 * ## What "normalised" buys
 *
 * Every consumer would otherwise repeat the same four defensive reads: `duration` is `NaN`
 * before `loadedmetadata` and `Infinity` for an unbounded resource, `currentTime` can land
 * a hair past `duration`, `buffered`/`seekable` throw when indexed out of range, and
 * `el.error` can be **`null` inside an `error` handler** in some engines. A snapshot taken
 * here is guaranteed finite, non-negative and internally consistent, so a `NaN` can never
 * reach a CSS custom property or `MediaSession.setPositionState`.
 *
 * ## Two things this module must never contain
 *
 * 1. **No `visibilitychange` listener.** On Android, minimising the Mini App calls
 *    `WebView.onPause()`, which fires `visibilitychange` with `document.hidden === true`.
 *    That is the exact moment the user expects the music to keep playing — it is the
 *    product's whole reason to exist. `bl/no-visibility-pause` catches the `pause()` form
 *    of the mistake; the absence of the listener is asserted by source grep in
 *    `events.test.ts`.
 * 2. **No `requestAnimationFrame`.** rAF does not fire on a hidden page. A rAF-driven
 *    progress signal freezes the moment the app is minimised, and anything downstream of
 *    it — the queue advance most of all — stops with it. The periodic signal here is a
 *    `setInterval`, because Telegram deliberately leaves `webView.pauseTimers()` commented
 *    out and timers keep running while hidden.
 *
 * The `ended` event, not a timer and not rAF, is what advances the queue. The interval
 * below exists only so a *position* readout stays live between `timeupdate`s (which the
 * spec allows to be as sparse as one every 250 ms, and which some engines throttle harder
 * while hidden).
 */

import { audio, type MediaElementLike } from './element';

import { bufferedAhead, bufferedEnd } from './duration';

/* ══ event vocabulary ══════════════════════════════════════════════════════════════ */

/**
 * The DOM events subscribed to, in the order they are registered.
 *
 * This is the complete list from `plan.md` T24. Deliberately absent: `loadstart`,
 * `emptied`, `canplay`, `canplaythrough`, `volumechange` (nothing in the app reacts to
 * them) and `visibilitychange` (which is not a media event at all — see the module
 * header).
 */
export const MEDIA_EVENTS = [
  'loadedmetadata',
  'durationchange',
  'timeupdate',
  'progress',
  'waiting',
  'stalled',
  'suspend',
  'playing',
  'pause',
  'ended',
  'error',
  'seeked',
  'ratechange',
] as const;

export type MediaEventName = (typeof MEDIA_EVENTS)[number];

/** Where an engine event came from. `'tick'` is the interval, not a DOM event. */
export type EngineEventSource = MediaEventName | 'tick';

/**
 * The normalised event kinds.
 *
 * One per DOM event, plus the fact that the interval also reports as `'time'` — a
 * consumer that only wants "the position moved" subscribes to one kind and gets both the
 * element's `timeupdate` and the timer, with no idea which produced it.
 */
export type EngineEventKind =
  | 'metadata'
  | 'duration'
  | 'time'
  | 'progress'
  | 'waiting'
  | 'stalled'
  | 'suspend'
  | 'playing'
  | 'pause'
  | 'ended'
  | 'error'
  | 'seeked'
  | 'ratechange';

/** DOM event name → normalised kind. */
export const EVENT_KIND: Readonly<Record<MediaEventName, EngineEventKind>> = {
  loadedmetadata: 'metadata',
  durationchange: 'duration',
  timeupdate: 'time',
  progress: 'progress',
  waiting: 'waiting',
  stalled: 'stalled',
  suspend: 'suspend',
  playing: 'playing',
  pause: 'pause',
  ended: 'ended',
  error: 'error',
  seeked: 'seeked',
  ratechange: 'ratechange',
};

/* ══ snapshot ══════════════════════════════════════════════════════════════════════ */

/**
 * A scrubbed reading of the element. Every number is finite and non-negative; every
 * relationship the UI relies on (`positionS <= durationS` when the duration is known) is
 * already enforced.
 */
export interface EngineSnapshot {
  /** `currentTime`, clamped into `[0, durationS]`. Never `NaN`. */
  readonly positionS: number;
  /** The element's own duration when it is finite and positive, else `0`. */
  readonly durationS: number;
  /** `false` while the element still reports `NaN`/`Infinity`/`0` for its duration. */
  readonly durationKnown: boolean;
  /** Seconds of contiguous audio ahead of the playhead; `0` in a gap. */
  readonly bufferedAheadS: number;
  /** Absolute end of the buffered range holding the playhead; `null` in a gap. */
  readonly bufferedEndS: number | null;
  readonly paused: boolean;
  readonly ended: boolean;
  /** `HTMLMediaElement.readyState` — see `READY_STATE` in `element.ts`. */
  readonly readyState: number;
  /** `HTMLMediaElement.networkState` — see `NETWORK_STATE` in `element.ts`. */
  readonly networkState: number;
  /** `playbackRate`, never `0` and never `NaN` (both make `setPositionState` throw). */
  readonly playbackRate: number;
  /** `el.error?.code ?? null`. `null` in an `error` handler means "unknown failure". */
  readonly errorCode: number | null;
}

/** An event from the engine: what happened, when, and the state it left behind. */
export interface EngineEvent {
  readonly kind: EngineEventKind;
  readonly source: EngineEventSource;
  /** `Date.now()` at dispatch, so consumers can age a stall without a second clock. */
  readonly at: number;
  readonly snapshot: EngineSnapshot;
}

export type EngineEventListener = (event: EngineEvent) => void;

/** Removes a subscription. Calling it more than once is safe. */
export type Unsubscribe = () => void;

function readNumber(value: number, fallback: number): number {
  return Number.isFinite(value) && value >= 0 ? value : fallback;
}

/**
 * Takes a snapshot of `el`.
 *
 * Each property is read inside the same guard the UI would otherwise have to write. Note
 * that `durationS` is the **element's** duration only — the server fallback lives in
 * `duration.ts`'s {@link effectiveDuration}, because the engine has no business knowing
 * the catalogue.
 */
export function readSnapshot(el: MediaElementLike = audio): EngineSnapshot {
  const rawDuration = el.duration;
  const durationKnown = Number.isFinite(rawDuration) && rawDuration > 0;
  const durationS = durationKnown ? rawDuration : 0;

  const rawPosition = readNumber(el.currentTime, 0);
  const positionS = durationKnown ? Math.min(rawPosition, durationS) : rawPosition;

  const rawRate = el.playbackRate;
  const playbackRate = Number.isFinite(rawRate) && rawRate > 0 ? rawRate : 1;

  return {
    positionS,
    durationS,
    durationKnown,
    bufferedAheadS: bufferedAhead(el),
    bufferedEndS: bufferedEnd(el),
    paused: el.paused !== false,
    ended: el.ended === true,
    readyState: readNumber(el.readyState, 0),
    networkState: readNumber(el.networkState, 0),
    playbackRate,
    errorCode: el.error?.code ?? null,
  };
}

/* ══ subscription ══════════════════════════════════════════════════════════════════ */

/**
 * How often the interval re-reads the position while playing.
 *
 * `timeupdate` is specified as "about 4Hz" but engines are allowed to throttle it, and
 * Telegram's Android webview does while backgrounded. 500 ms is fine for a playhead that
 * is redrawn by the UI layer and cheap enough to leave running for a whole set.
 */
export const PROGRESS_TICK_MS = 500;

export interface SubscribeOptions {
  /** The element to observe; defaults to the app singleton. */
  readonly el?: MediaElementLike;
  /**
   * Interval for the synthetic `'time'` event, in ms. `0` disables it — used by
   * `recovery.ts`, which must not be fed its own heartbeat, and by tests that do not want
   * a timer.
   */
  readonly tickMs?: number;
  /** Clock, for tests. Defaults to `Date.now`. */
  readonly now?: () => number;
}

/**
 * Subscribes `listener` to the normalised stream and returns an unsubscribe.
 *
 * May be called any number of times; each subscription owns its own listeners and its own
 * interval. The listener is called synchronously from the DOM event, so a throwing
 * listener would otherwise take out the element's event dispatch for the remaining
 * handlers — hence the per-call `try/catch`.
 */
export function subscribeEngineEvents(
  listener: EngineEventListener,
  options: SubscribeOptions = {},
): Unsubscribe {
  const el = options.el ?? audio;
  const tickMs = options.tickMs ?? PROGRESS_TICK_MS;
  const now = options.now ?? Date.now;

  const emit = (kind: EngineEventKind, source: EngineEventSource): void => {
    const event: EngineEvent = { kind, source, at: now(), snapshot: readSnapshot(el) };
    try {
      listener(event);
    } catch {
      // A consumer's bug must not stop the element's other handlers from running, and
      // must not surface as an unhandled error inside a media event dispatch.
    }
  };

  const registered: { name: MediaEventName; handler: (event: Event) => void }[] = [];
  for (const name of MEDIA_EVENTS) {
    const kind = EVENT_KIND[name];
    const handler = (): void => {
      emit(kind, name);
    };
    el.addEventListener(name, handler);
    registered.push({ name, handler });
  }

  // Timer, never rAF. See the module header.
  let timer: ReturnType<typeof setInterval> | null = null;
  if (tickMs > 0) {
    timer = setInterval(() => {
      if (el.paused) return;
      emit('time', 'tick');
    }, tickMs);
  }

  let live = true;
  return () => {
    if (!live) return;
    live = false;
    for (const { name, handler } of registered) el.removeEventListener(name, handler);
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}
